<div class="crew-schedule-toolbar">
    <span class="crew-schedule-range-label">
        Crew schedule: {{ gridStart() | date: 'mediumDate' }} – {{ gridRangeEnd() | date: 'mediumDate' }}
        ({{ crewScheduleWeekdayCount }} weekdays)
    </span>
    <span class="text-muted crew-schedule-session-note">Changes save automatically and are shared with everyone. Projected hours refresh from Vista on load.</span>
    <label class="crew-schedule-division-filter">
        <span>Group by</span>
        <select
            class="form-select form-select-sm"
            [ngModel]="groupBy()"
            (ngModelChange)="onGroupByChange($event)"
            [disabled]="isLoading()"
        >
            <option value="division">By Division</option>
            <option value="job">By Job</option>
        </select>
    </label>
    <label class="crew-schedule-division-filter">
        <span>Division</span>
        <select
            class="form-select form-select-sm"
            [ngModel]="selectedDivisionId()"
            (ngModelChange)="onDivisionFilterChange($event)"
            [disabled]="isLoading()"
        >
            <option value="">All Divisions</option>
            @for (division of divisionOptions(); track division.id) {
                <option [value]="division.id">{{ division.name }}</option>
            }
        </select>
    </label>
    <label class="crew-schedule-include-all" title="Also list phases that have no remaining hours so a crew can be assigned to them">
        <input
            type="checkbox"
            [ngModel]="includeAllPhases()"
            (ngModelChange)="onIncludeAllPhasesChange($event)"
            [disabled]="isLoading()"
        />
        <span>Show phases with no remaining hours</span>
    </label>
    <button type="button" class="btn btn-outline-secondary btn-sm" (click)="refresh()" [disabled]="isLoading()">
        {{ isLoading() ? 'Loading…' : 'Refresh' }}
    </button>
</div>

@if (isLoading()) {
    <p class="text-muted crew-schedule-status">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        Loading crew schedule…
    </p>
} @else if (loadError()) {
    <p class="text-danger crew-schedule-status">{{ loadError() }}</p>
} @else if (!hasVisibleForemen() && groupBy() !== 'job') {
    <p class="text-muted crew-schedule-status">
        {{ selectedDivisionId() ? 'No foremen found for the selected division.' : 'No foremen found.' }}
    </p>
} @else {
    <div class="crew-schedule-scroll" appAutoSize [padding]="25" [minHeight]="300" [limitAfterMax]="true">
        <div class="crew-schedule-grid" [style.--crew-schedule-cols]="gridTemplateColumns()">
            <div class="crew-schedule-header-row">
                <div class="crew-schedule-name-col crew-schedule-header-cell">Foreman</div>
                @for (col of gridColumns(); track col.key) {
                    @if (col.type === 'weekend') {
                        <button
                            type="button"
                            class="crew-schedule-weekend-gap crew-schedule-weekend-toggle crew-schedule-header-cell"
                            [class.crew-schedule-weekend-has-entries]="weekendHasEntries(col.dates)"
                            title="Weekend — click to schedule weekend work"
                            (click)="toggleWeekend(col.key)"
                        >
                            <span class="crew-schedule-weekend-expand-icon">+</span>
                        </button>
                    } @else if (col.type === 'weekend-day') {
                        <div class="crew-schedule-day-col crew-schedule-header-cell crew-schedule-weekend-day-header">
                            <span class="crew-schedule-weekend-day-label">{{ col.label }}</span>
                            <button
                                type="button"
                                class="crew-schedule-weekend-collapse"
                                title="Collapse weekend"
                                (click)="toggleWeekend(col.weekendKey)"
                            >
                                ×
                            </button>
                        </div>
                    } @else {
                        <div class="crew-schedule-day-col crew-schedule-header-cell" [class.crew-schedule-day-today]="col.isToday">
                            {{ col.label }}
                        </div>
                    }
                }
            </div>

            @for (group of foremanGroups(); track group.key) {
                <div
                    class="crew-schedule-division-header"
                    [class.crew-schedule-division-header-empty]="!group.rows.length && !!group.groupJobId"
                >
                    <span class="crew-schedule-division-header-sticky">
                        <span class="crew-schedule-division-header-label">{{ group.label }}</span>
                        <span class="crew-schedule-division-header-count">({{ group.rows.length }})</span>
                        @if (!group.rows.length && group.groupJobId && canEdit()) {
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-primary crew-schedule-header-assign-btn"
                                (click)="openJobAssignmentModal(group.groupJobId!)"
                            >
                                Assign a crew
                            </button>
                        }
                    </span>
                </div>
                @if (!group.rows.length && group.groupJobId) {
                    <div class="crew-schedule-empty-group-row">
                        <span class="crew-schedule-empty-group-sticky">
                            <span class="crew-schedule-empty-group-note">No crews scheduled</span>
                        </span>
                    </div>
                }
                @for (row of group.rows; track row.hrRef) {
                <div class="crew-schedule-foreman-block">
                    <div class="crew-schedule-foreman-header-row">
                        <button type="button" class="crew-schedule-foreman-toggle" (click)="toggleForeman(row.hrRef)">
                            <span class="crew-schedule-foreman-sticky">
                                <span class="crew-schedule-toggle-icon">{{ row.expanded ? '▼' : '▶' }}</span>
                                <span class="crew-schedule-foreman-name">{{ row.name }}</span>
                            </span>
                            <span class="text-muted crew-schedule-role">{{ row.jobTitle }}</span>
                            <span class="text-muted crew-schedule-meta-sep">|</span>
                            <span class="text-muted crew-schedule-crew-count">{{ row.totalCrewCount }}-person crew</span>
                            <span class="text-muted crew-schedule-meta-sep">|</span>
                            <span class="text-muted crew-schedule-off-days">{{ row.scheduledOffDaysLabel }}</span>
                        </button>
                    </div>

                    @if (row.expanded) {
                        <div class="crew-schedule-summary">
                            <span>{{ row.summaryText }}</span>
                            @if (row.warningText) {
                                <span class="text-warning crew-schedule-warning">{{ row.warningText }}</span>
                            }
                            @if (row.state.error) {
                                <span class="text-danger">{{ row.state.error }}</span>
                            }
                        </div>

                        <div class="crew-schedule-data-row">
                            <div class="crew-schedule-name-col crew-schedule-foreman-label">
                                <span class="text-muted">Assignment</span>
                            </div>
                            @for (cell of row.gridCells; track cell.key) {
                                @if (cell.type === 'weekend') {
                                    <button
                                        type="button"
                                        class="crew-schedule-weekend-gap crew-schedule-weekend-toggle"
                                        [class.crew-schedule-weekend-has-entries]="cell.weekendHasEntries"
                                        [title]="cell.weekendHasEntries ? 'Weekend work scheduled — click to view' : 'Weekend — click to schedule weekend work'"
                                        (click)="toggleWeekend(cell.weekendKey!)"
                                    >
                                        @if (cell.weekendHasEntries) {
                                            <span class="crew-schedule-weekend-dot"></span>
                                        }
                                    </button>
                                } @else if (cell.type === 'weekend-day') {
                                    <div
                                        class="crew-schedule-day-col crew-schedule-cell crew-schedule-weekend-day-cell"
                                        [class.crew-schedule-cell-holiday]="cell.isHoliday && !cell.weekendEntry && !cell.maskedJobId"
                                        [class.crew-schedule-cell-weekend-projected]="!!cell.weekendEntry"
                                        [class.crew-schedule-cell-masked]="!!cell.maskedJobId"
                                        [title]="weekendCellTooltip(cell)"
                                    >
                                        @if (cell.weekendEntry) {
                                            <div class="crew-schedule-projection-badge">
                                                @if (cell.showClearButton) {
                                                    <button
                                                        type="button"
                                                        class="crew-schedule-clear-btn"
                                                        title="Remove this weekend work"
                                                        (click)="clearWeekendEntry(row.hrRef, cell.date!)"
                                                    >
                                                        ×
                                                    </button>
                                                }
                                                <span class="crew-schedule-projection-job">{{ cell.weekendEntry.vpJobId }}</span>
                                                <span class="crew-schedule-projection-phase">{{ cell.weekendEntry.phase }}</span>
                                                <label class="crew-schedule-weekend-hours-label">
                                                    <span>Hours</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.5"
                                                        class="form-control form-control-sm crew-schedule-weekend-hours"
                                                        [ngModel]="cell.weekendEntry.hours"
                                                        (ngModelChange)="onWeekendHoursChange(row.hrRef, cell.date!, $event)"
                                                        [disabled]="!canEdit()"
                                                    />
                                                </label>
                                            </div>
                                        } @else if (cell.maskedJobId) {
                                            <span class="crew-schedule-masked-job" [title]="jobLabel(cell.maskedJobId)">
                                                {{ cell.maskedJobId }}
                                            </span>
                                        } @else if (cell.weekendPicker) {
                                            <div class="crew-schedule-select-wrap">
                                                <select
                                                    class="form-select form-select-sm crew-schedule-select"
                                                    [ngModel]="cell.weekendPicker.jobId"
                                                    (ngModelChange)="onWeekendJobChange(row.hrRef, cell.date!, $event)"
                                                    [compareWith]="compareJobIds"
                                                    [title]="cell.weekendPicker.jobId ? jobLabel(cell.weekendPicker.jobId) : 'Select job'"
                                                    [disabled]="!canEdit() || cell.weekendPicker.loadingPhases"
                                                >
                                                    <option value="">Job…</option>
                                                    @for (job of activeJobs(); track job.id) {
                                                        <option [value]="normalizeJobId(job.id)">{{ normalizeJobId(job.id) }} — {{ job.name }}</option>
                                                    }
                                                </select>
                                            </div>
                                            <div class="crew-schedule-select-wrap">
                                                <select
                                                    class="form-select form-select-sm crew-schedule-select"
                                                    [ngModel]="cell.weekendPicker.phase"
                                                    (ngModelChange)="onWeekendPhaseChange(row.hrRef, cell.date!, $event, cell.weekendDefaultHours!)"
                                                    [compareWith]="comparePhases"
                                                    [disabled]="
                                                        !canEdit() ||
                                                        !cell.weekendPicker.jobId ||
                                                        cell.weekendPicker.loadingPhases ||
                                                        !cell.weekendPicker.phases.length
                                                    "
                                                >
                                                    <option value="">
                                                        @if (cell.weekendPicker.loadingPhases) {
                                                            Loading phases…
                                                        } @else if (!cell.weekendPicker.jobId) {
                                                            Phase…
                                                        } @else if (!cell.weekendPicker.phases.length) {
                                                            No phases available
                                                        } @else {
                                                            Phase…
                                                        }
                                                    </option>
                                                    @for (phase of cell.weekendPicker.phases; track phase.phase) {
                                                        <option [value]="phase.phase">{{ phaseLabel(phase) }}</option>
                                                    }
                                                </select>
                                            </div>
                                        } @else if (cell.isHoliday) {
                                            <span class="crew-schedule-holiday-badge">HOL</span>
                                        }
                                    </div>
                                } @else {
                                    <div
                                        class="crew-schedule-day-col crew-schedule-cell"
                                        [class.crew-schedule-cell-today]="cell.isToday"
                                        [class.crew-schedule-cell-holiday]="cell.isHoliday"
                                        [class.crew-schedule-cell-projected]="cell.assignment && cell.assignment.hoursScheduled > 0 && !cell.isOverEstimate && !cell.isOverHours"
                                        [class.crew-schedule-cell-over-estimate]="cell.isOverEstimate"
                                        [class.crew-schedule-cell-over-hours]="cell.isOverHours"
                                        [class.crew-schedule-cell-shared]="cell.isShared"
                                        [class.crew-schedule-cell-idle]="!cell.maskedJobId && !cell.dayPicker && (!cell.assignment || cell.assignment.hoursScheduled <= 0) && !cell.isHoliday"
                                        [class.crew-schedule-cell-masked]="!!cell.maskedJobId"
                                        [title]="cellTooltip(cell)"
                                    >
                                        @if (cell.maskedJobId) {
                                            <span class="crew-schedule-masked-job" [title]="jobLabel(cell.maskedJobId)">
                                                {{ cell.maskedJobId }}
                                            </span>
                                        } @else if (cell.isHoliday && (!cell.assignment || cell.assignment.hoursScheduled <= 0)) {
                                            <span class="crew-schedule-holiday-badge">HOL</span>
                                        } @else if (cell.dayPicker) {
                                            <div class="crew-schedule-select-wrap">
                                                <select
                                                    class="form-select form-select-sm crew-schedule-select"
                                                    [ngModel]="cell.dayPicker.jobId"
                                                    (ngModelChange)="onDayJobChange(row.hrRef, cell.date!, $event)"
                                                    [compareWith]="compareJobIds"
                                                    [title]="cell.dayPicker.jobId ? jobLabel(cell.dayPicker.jobId) : 'Select job'"
                                                    [disabled]="!canEdit() || cell.dayPicker.loadingPhases || row.state.loadingProjection"
                                                >
                                                    <option value="">Job…</option>
                                                    @for (job of activeJobs(); track job.id) {
                                                        <option [value]="normalizeJobId(job.id)">{{ normalizeJobId(job.id) }} — {{ job.name }}</option>
                                                    }
                                                </select>
                                            </div>
                                            @if (cell.dayPicker.jobId && !isActiveJob(cell.dayPicker.jobId)) {
                                                <span class="crew-schedule-selected-label">{{ jobLabel(cell.dayPicker.jobId) }}</span>
                                            }
                                            <div class="crew-schedule-select-wrap">
                                                <select
                                                    class="form-select form-select-sm crew-schedule-select"
                                                    [ngModel]="cell.dayPicker.phase"
                                                    (ngModelChange)="onDayPhaseChange(row.hrRef, cell.date!, $event)"
                                                    [compareWith]="comparePhases"
                                                    [disabled]="
                                                        !canEdit() ||
                                                        !cell.dayPicker.jobId ||
                                                        cell.dayPicker.loadingPhases ||
                                                        row.state.loadingProjection ||
                                                        !cell.dayPicker.phases.length
                                                    "
                                                >
                                                    <option value="">
                                                        @if (cell.dayPicker.loadingPhases) {
                                                            Loading phases…
                                                        } @else if (!cell.dayPicker.jobId) {
                                                            Phase…
                                                        } @else if (!cell.dayPicker.phases.length) {
                                                            No phases available
                                                        } @else {
                                                            Phase…
                                                        }
                                                    </option>
                                                    @for (phase of cell.dayPicker.phases; track phase.phase) {
                                                        <option [value]="phase.phase">{{ phaseLabel(phase) }}</option>
                                                    }
                                                </select>
                                            </div>
                                            <div class="crew-schedule-days-wrap">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    class="form-control form-control-sm crew-schedule-days-input"
                                                    placeholder="Days (optional)"
                                                    title="Force the crew onto this phase for a set number of working days, even after its remaining hours run out. Leave blank to schedule by remaining hours."
                                                    [ngModel]="cell.dayPicker.days"
                                                    (ngModelChange)="onDayDaysChange(row.hrRef, cell.date!, $event)"
                                                    [disabled]="!canEdit() || !cell.dayPicker.phase || row.state.loadingProjection"
                                                />
                                            </div>
                                            @if (dayPickerOverlapNote(row, cell)) {
                                                <span
                                                    class="crew-schedule-overlap-note"
                                                    [title]="dayPickerOverlapTooltip(row, cell)"
                                                >
                                                    {{ dayPickerOverlapNote(row, cell) }}
                                                </span>
                                            }
                                            <div class="crew-schedule-day-picker-actions">
                                                <button
                                                    type="button"
                                                    class="btn btn-sm btn-primary crew-schedule-day-picker-add"
                                                    title="Add this assignment"
                                                    [disabled]="!canEdit() || !cell.dayPicker.jobId || !cell.dayPicker.phase || row.state.loadingProjection"
                                                    (click)="confirmDayAssignment(row.hrRef, cell.date!)"
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    type="button"
                                                    class="crew-schedule-day-picker-cancel"
                                                    title="Cancel"
                                                    (click)="closeDayPicker(row.hrRef, cell.date!)"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            @if (row.state.loadingProjection) {
                                                <span class="text-muted crew-schedule-loading">Projecting…</span>
                                            }
                                        } @else if (cell.showAddButton) {
                                            <button
                                                type="button"
                                                class="crew-schedule-add-btn"
                                                title="Schedule an assignment starting this day"
                                                (click)="openDayPicker(row.hrRef, cell.date!)"
                                            >
                                                +
                                            </button>
                                        } @else if (cell.assignment && cell.assignment.hoursScheduled > 0) {
                                            <div class="crew-schedule-projection-badge">
                                                @if (cell.showClearButton && cell.segmentId) {
                                                    <button
                                                        type="button"
                                                        class="crew-schedule-clear-btn"
                                                        title="Clear this day and all future days for this assignment"
                                                        (click)="clearFromDay(row.hrRef, cell.segmentId!, cell.date!)"
                                                    >
                                                        ×
                                                    </button>
                                                }
                                                <span class="crew-schedule-projection-job">{{ cell.assignment.vpJobId }}</span>
                                                <span class="crew-schedule-projection-phase">{{ cell.assignment.phase }}</span>
                                                @if (cell.isShared) {
                                                    <span
                                                        class="crew-schedule-shared-badge"
                                                        title="Shared job/phase — multiple crews drawing from one hour pool"
                                                    >
                                                        {{ cell.assignment.sharedCrewCount }} crews share pool
                                                    </span>
                                                }
                                                <span class="crew-schedule-projection-remaining">
                                                    {{ cell.assignment.remainingHoursAtStart | number: '1.0-1' }}h →
                                                    {{ cell.assignment.remainingHoursAtEnd | number: '1.0-1' }}h left
                                                </span>
                                                <span class="crew-schedule-projection-hours">
                                                    {{ cell.assignment.hoursScheduled | number: '1.0-1' }}h
                                                    ({{ cell.assignment.availableCrewCount }}/{{ cell.assignment.totalCrewCount }})
                                                </span>
                                            </div>
                                        } @else if (cell.isHoliday) {
                                            <span class="crew-schedule-holiday-badge">HOL</span>
                                        }
                                        @if (cell.offMembers?.length) {
                                            <div class="crew-schedule-off-strip" [title]="offStripTooltip(cell)">
                                                @for (m of cell.offMembers!.slice(0, 4); track m.hrRef) {
                                                    <span
                                                        class="crew-schedule-off-chip"
                                                        [class.crew-schedule-off-approved]="m.status === 'Approved'"
                                                        [class.crew-schedule-off-pending]="m.status === 'Pending'"
                                                    >{{ m.initials }}</span>
                                                }
                                                @if (cell.offMembers!.length > 4) {
                                                    <span class="crew-schedule-off-chip crew-schedule-off-more">+{{ cell.offMembers!.length - 4 }}</span>
                                                }
                                            </div>
                                        }
                                    </div>
                                }
                            }
                        </div>
                    }
                </div>
                }
            }
        </div>
    </div>
}
